import type preact from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { api, ApiError, type Me } from './api.js'
import { NAV_ITEMS, SECONDARY_ITEMS } from './nav.js'
import { Anmelden } from './routes/Anmelden.js'
import { Einladung } from './routes/Einladung.js'
import { Start } from './routes/Start.js'
import { direktAktionen } from './start-aktionen.js'
import { lazy, Suspense } from 'preact/compat'
import { Entsperren } from './crypto/Entsperren.js'
import { e2ee, vorwaermen } from './crypto/lazy.js'
// Nur der Typ — zur Laufzeit wird der Speicher weiterhin lazy geladen, sonst
// zöge er die Krypto ins Startbundle (ARCHITECTURE.md §6).
import type * as Keystore from './crypto/keystore.js'
import { WiederherstellungsWarnung } from './crypto/Anker.js'
import { StandortBanner } from './routes/StandortBanner.js'
import { connect, disconnect, onReconnect } from './ws.js'
import { useOnline } from './useOnline.js'
import { quellenVergessen } from './media/quellen.js'
import { usePrefs } from './prefs.js'

/**
 * App-Shell.
 *
 * Drei Zustände: Einladung, Anmeldung, angemeldet. Alles andere kommt ab Phase 4
 * als lazy geladene Routen dazu — das Startbudget von 90 kB (ARCHITECTURE.md §6)
 * trägt nur die Shell, die Familienübersicht, den Status und SOS.
 */
export function App() {
  const online = useOnline()
  const perf = usePrefs((s) => s.perf)
  const [route, setRoute] = useState(currentHash())
  const [me, setMe] = useState<Me | null | undefined>(undefined)
  /** undefined = noch nicht geprüft, false = gesperrt, true = entsperrt */
  const [entsperrt, setEntsperrt] = useState<boolean | undefined>(undefined)
  const [neuerSpeicher, setNeuerSpeicher] = useState(false)
  /** Ist der Passkey für den Speicher DIESES Geräts als Weg eingerichtet? */
  const [passkeyWeg, setPasskeyWeg] = useState(false)
  /** Vom Passkey geliefertes Geheimnis — entsperrt ohne Passwort. */
  const [prf, setPrf] = useState<ArrayBuffer | null>(null)

  useEffect(() => {
    const onHash = () => setRoute(currentHash())
    addEventListener('hashchange', onHash)
    return () => removeEventListener('hashchange', onHash)
  }, [])

  useEffect(() => {
    api.get<Me>('/me')
      .then(setMe)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.needsLogin) setMe(null)
        else setMe(null)
      })
  }, [])

  // Live-Verbindung nur, solange jemand angemeldet ist.
  useEffect(() => {
    if (!me) return
    connect()
    /*
     * Krypto im Hintergrund nachladen, damit der Chat nicht wartet — außer im
     * Sparsam-Modus.
     *
     * Der Krypto-Chunk sind 92 kB gzip, entpackt und geparst ein Vielfaches
     * davon. Wer im Datensparmodus unterwegs ist oder ein Gerät mit 2 GB RAM
     * hat, soll das nicht auf Verdacht bezahlen: Beim Öffnen des Chats wird es
     * ohnehin geladen, dann eben mit einer sichtbaren Wartezeit statt mit
     * einer unsichtbaren Rechnung (Anf. 66/67).
     */
    if (perf !== 'low') vorwaermen()
    return () => disconnect()
  }, [me, perf])

  /*
   * Delta-Sync nach einem Verbindungsabbruch (Anf. 30).
   *
   * Der WebSocket meldet nur, was passiert, solange er steht. Nach einem
   * Abbruch fehlt alles dazwischen. Statt beim Reconnect ALLES neu zu laden —
   * auf einer Mobilverbindung mehrere Sekunden für Daten, von denen sich fast
   * nichts geändert hat — holt der Client nur die Lücke.
   *
   * Der Cursor ist die SERVERZEIT aus der letzten Antwort. Die Uhr des
   * Telefons wäre gefährlich: Geht sie vor, würden Änderungen übersprungen.
   */
  useEffect(() => {
    if (!me) return
    return onReconnect(() => {
      const seit = localStorage.getItem('fh-sync-cursor')
      if (!seit) return

      void api.get<{ now: string; truncated: boolean }>(
        `/sync?since=${encodeURIComponent(seit)}`,
      ).then((delta) => {
        localStorage.setItem('fh-sync-cursor', delta.now)
        // Bei zu vielen Änderungen ist ein Delta kein Delta mehr. Dann wird
        // die Seite neu geladen, statt etwas Unvollständiges einzuspielen.
        if (delta.truncated) location.reload()
      }).catch(() => {
        // Klappt der Sync nicht, ist der Cursor unbrauchbar geworden: Beim
        // nächsten Versuch würde eine Lücke entstehen, die niemand bemerkt.
        localStorage.removeItem('fh-sync-cursor')
      })
    })
  }, [me])

  // Beim Anmelden einen Startpunkt setzen — vorher gibt es nichts nachzuholen.
  useEffect(() => {
    if (me) localStorage.setItem('fh-sync-cursor', new Date().toISOString())
  }, [me?.personId])

  // Ist der Schlüsselspeicher auf diesem Gerät schon eingerichtet — und ist der
  // Passkey als Weg hinein hinterlegt?
  useEffect(() => {
    if (!me) { setEntsperrt(undefined); return }
    void (async () => {
      const { istEingerichtet, wege } = await import('./crypto/keystore.js')
      setNeuerSpeicher(!(await istEingerichtet()))
      setPasskeyWeg((await wege()).includes('prf'))
      setEntsperrt(false)
    })()
  }, [me])

  // Hat der Passkey ein Geheimnis geliefert, entsperrt die App ohne Rückfrage.
  // Das ist der geplante Hauptweg (ADR-004): auflegen, drin sein. Klappt es
  // nicht, fällt die App auf die Passphrase zurück, statt hängen zu bleiben.
  useEffect(() => {
    if (!me || !prf || entsperrt !== false) return
    let abgebrochen = false
    void entsperrenMit('prf', prf, me.personId)
      .then(() => { if (!abgebrochen) setEntsperrt(true) })
      .catch(() => { if (!abgebrochen) setPrf(null) })
    return () => { abgebrochen = true }
  }, [me, prf, entsperrt])

  const einladungsToken = route.startsWith('/einladung/') ? route.slice('/einladung/'.length) : null

  if (einladungsToken) {
    return <Einladung token={einladungsToken} onDone={(m) => { setMe(m); location.hash = '#familie' }} />
  }
  if (me === undefined) return <Startbildschirm />
  if (me === null) {
    return (
      <Anmelden onDone={(m, prfGeheimnis) => {
        setPrf(prfGeheimnis ?? null)
        setMe(m)
        location.hash = '#familie'
      }} />
    )
  }

  // Der Schlüsselspeicher wird beim ersten Öffnen entsperrt. Ohne ihn liefe der
  // Chat blind — deshalb kommt der Schritt vor der Oberfläche, nicht danach.
  if (entsperrt === false) {
    // Wartet auf den Effekt oben: Er entsperrt still mit dem Passkey-Geheimnis.
    if (prf) return <Startbildschirm />

    return (
      <Entsperren
        neu={neuerSpeicher}
        passkeyWeg={passkeyWeg}
        mitSatz={async (satz) => { await entsperrenMit('passphrase', satz, me.personId) }}
        mitPasskey={async () => {
          const { passkeyGeheimnis } = await import('./crypto/passkeyEntsperren.js')
          const geheimnis = await passkeyGeheimnis()
          if (!geheimnis) return false
          await entsperrenMit('prf', geheimnis, me.personId)
          return true
        }}
        // Läuft auf einem bereits offenen Speicher: Der Speicherschlüssel wird
        // ein zweites Mal verschlüsselt, diesmal mit dem Geheimnis des Passkeys.
        // Die Inhalte bleiben unberührt, der Satz gilt weiter.
        passkeyNachruesten={async () => {
          const { passkeyGeheimnis } = await import('./crypto/passkeyEntsperren.js')
          const { wegHinzufuegen } = await import('./crypto/keystore.js')
          const geheimnis = await passkeyGeheimnis()
          if (!geheimnis) return false
          await wegHinzufuegen('prf', geheimnis)
          return true
        }}
        onFertig={() => setEntsperrt(true)}
      />
    )
  }
  if (entsperrt === undefined) return <Startbildschirm />

  const unterbereich = SECONDARY_ITEMS.find((i) => i.id === route)
  const aktiv = NAV_ITEMS.find((i) => i.id === route)
    ?? (unterbereich ? NAV_ITEMS.find((i) => i.id === 'mehr')! : NAV_ITEMS[0])

  return (
    <div class="fh-app">
      {/*
        Steht VOR dem Offline-Banner: Eine laufende Wiederherstellung ist das
        Wichtigste, was die App jemand mitteilen kann (ADR-008, Risiko T13).
      */}
      <WiederherstellungsWarnung />

      {/*
        Der Standort-Banner steht HIER und nicht im Standort-Bereich. Wer ihn
        dorthin verschiebt, macht ihn unsichtbar, sobald jemand im Chat ist —
        und hebt damit ADR-009, Bedingung 3 auf.
      */}
      <StandortBanner />

      {!online && (
        <div class="fh-banner fh-banner--offline" role="status">
          {/* Symbol UND Text — Farbe trägt die Information nie allein (Anf. 58). */}
          <span aria-hidden="true">🟠</span>
          <span>Keine Internetverbindung. Du siehst gespeicherte Inhalte.</span>
        </div>
      )}

      <main class="fh-main" id="inhalt" tabIndex={-1}>
        <h1 class="fh-h1">{unterbereich?.label ?? aktiv.label}</h1>
        <Bereich
          route={unterbereich?.id ?? aktiv.id}
          me={me}
          onLoggedOut={() => setMe(null)}
        />
      </main>

      <nav class="fh-nav" aria-label="Hauptbereiche">
        {NAV_ITEMS.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            class={`fh-nav__item${item.id === 'sos' ? ' fh-nav__item--sos' : ''}`}
            aria-current={item.id === aktiv.id ? 'page' : undefined}
          >
            <span class="fh-nav__icon" aria-hidden="true">{item.icon}</span>
            <span>{item.label}</span>
          </a>
        ))}
      </nav>
    </div>
  )
}

/*
 * Nachgeladene Bereiche.
 *
 * Der Chat stand bis Phase 14 versehentlich im Startbundle: Er war statisch
 * importiert, also lag sein Code in `index-*.js`. Das Budget „Chat ≤ 60 kB"
 * fand deshalb keinen einzigen Chunk — und meldete genau deswegen „ok". Ein
 * Budget, das nichts findet, prüft nichts; seit Phase 14 lässt der leere
 * Treffer die Prüfung scheitern (scripts/check-bundle-budget.mjs).
 */
const Chat = lazy(() => import('./routes/Chat.js').then((m) => ({ default: m.Chat })))
const Mitglieder = lazy(() =>
  import('./routes/Mitglieder.js').then((m) => ({ default: m.Mitglieder })))
const Geraete = lazy(() => import('./routes/Geraete.js').then((m) => ({ default: m.Geraete })))
const Dateien = lazy(() => import('./routes/Dateien.js').then((m) => ({ default: m.Dateien })))
const Anruf = lazy(() => import('./routes/Anruf.js').then((m) => ({ default: m.Anruf })))
const Standort = lazy(() =>
  import('./routes/Standort.js').then((m) => ({ default: m.Standort })))
const Status = lazy(() => import('./routes/Status.js').then((m) => ({ default: m.Status })))
const Sos = lazy(() => import('./routes/Sos.js').then((m) => ({ default: m.Sos })))
const Kalender = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Kalender })))
const Aufgaben = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Aufgaben })))
const Notizen = lazy(() =>
  import('./routes/Module.js').then((m) => ({ default: m.Notizen })))
const Fotos = lazy(() => import('./routes/Fotos.js').then((m) => ({ default: m.Fotos })))
const Suche = lazy(() => import('./routes/Suche.js').then((m) => ({ default: m.Suche })))
const Einstellungen = lazy(() =>
  import('./routes/Einstellungen.js').then((m) => ({ default: m.Einstellungen })))

function Bereich({ route, me, onLoggedOut }: {
  route: string; me: Me; onLoggedOut: () => void
}) {
  if (route === 'dateien') {
    return (
      <Suspense fallback={<p class="fh-muted">Wird geladen…</p>}>
        <Dateien />
      </Suspense>
    )
  }

  if (route === 'familie') {
    // Die Startseite ist NICHT lazy: Sie ist das Erste, was jemand sieht, und
    // ein Nachladen davor wäre genau die Wartezeit, die vermieden werden soll
    // (ARCHITECTURE.md §6).
    return <Start me={me} aktionen={direktAktionen()} />
  }

  // Die Module aus Phase 12. Alle lazy: Wer nie einen Termin anlegt, lädt
  // den Kalender nie (ARCHITECTURE.md §6).
  const module: Record<string, preact.JSX.Element> = {
    chat: <Chat me={me} />,
    mitglieder: <Mitglieder me={me} />,
    kalender: <Kalender me={me} />,
    aufgaben: <Aufgaben />,
    notizen: <Notizen />,
    fotos: <Fotos me={me} />,
    suche: <Suche />,
    einstellungen: <Einstellungen me={me} onGeloescht={onLoggedOut} />,
  }
  if (module[route]) {
    return (
      <Suspense fallback={<p class="fh-muted">Wird geladen…</p>}>
        {module[route]}
      </Suspense>
    )
  }

  if (route === 'sos') {
    return (
      <Suspense fallback={<p class="fh-muted">Wird geladen…</p>}>
        <Sos me={me} />
      </Suspense>
    )
  }

  if (route === 'status') {
    return (
      <Suspense fallback={<p class="fh-muted">Wird geladen…</p>}>
        <Status me={me} />
      </Suspense>
    )
  }

  if (route === 'standort') {
    return (
      <Suspense fallback={<p class="fh-muted">Wird geladen…</p>}>
        <Standort me={me} />
      </Suspense>
    )
  }

  if (route === 'anrufen') {
    return (
      <Suspense fallback={<p class="fh-muted">Wird geladen…</p>}>
        <Anruf me={me} />
      </Suspense>
    )
  }

  if (route === 'mehr') {
    return (
      <>
        <div class="fh-card">
          <h2 class="fh-h2">Angemeldet als {me.displayName}</h2>
          <p class="fh-muted">Rolle: {rollenName(me.role)}</p>
          <button
            class="fh-btn fh-btn--quiet"
            onClick={() => {
              // Beim Abmelden wird der Schlüsselspeicher geleert, nicht nur der
              // Schlüssel vergessen (SECURITY.md §3.5).
              void api.post('/auth/logout')
                // Signierte Bild-URLs hängen an der Sitzung. Nach einem
                // Kontowechsel auf demselben Gerät wären sie wertlos — der
                // Zwischenspeicher wüsste es nur nicht.
                .then(() => quellenVergessen())
                .then(() => import('./crypto/keystore.js'))
                .then((k) => k.loeschen())
                // Der Service Worker hält seit Phase 13 einen Daten-Cache.
                // Er gehört zum selben Aufräumen wie der Schlüsselspeicher:
                // Das Abmelden ist der Moment, in dem ein Gerät die Hand
                // wechseln könnte.
                .then(() => navigator.serviceWorker?.controller
                  ?.postMessage({ type: 'fh-abmelden' }))
                .then(onLoggedOut)
            }}
          >
            Abmelden
          </button>
        </div>
        <h2 class="fh-h2">Meine Geräte</h2>
        <Suspense fallback={<p class="fh-muted">Wird geladen…</p>}>
          <Geraete onLoggedOut={onLoggedOut} />
        </Suspense>

        <h2 class="fh-h2">Weitere Bereiche</h2>
        <ul class="fh-liste">
          {SECONDARY_ITEMS.map((item) => (
            <li key={item.id} class="fh-card">
              <a class="fh-btn fh-btn--quiet" href={`#${item.id}`}>
                <span aria-hidden="true">{item.icon}</span> {item.label}
              </a>
            </li>
          ))}
        </ul>

        <h2 class="fh-h2">Sicherheit</h2>
        <SicherheitsBereich me={me} />
      </>
    )
  }

  return (
    <div class="fh-card">
      <h2 class="fh-h2">Noch nicht gebaut</h2>
      <p class="fh-muted">
        Der Bereich „{NAV_ITEMS.find((i) => i.id === route)?.label}" kommt in einer
        späteren Phase. Das Gerüst steht, die Funktion noch nicht.
      </p>
    </div>
  )
}

/**
 * Entsperrt den Schlüsselspeicher und startet die Krypto.
 *
 * Beide Wege enden hier: Passkey-Geheimnis (PRF) oder Passphrase. Der
 * Unterschied liegt nur im ersten Argument — alles danach ist gleich.
 */
async function entsperrenMit(
  methode: 'prf' | 'passphrase',
  geheimnis: ArrayBuffer | string,
  personId: string,
): Promise<void> {
  const speicher = await import('./crypto/keystore.js')
  await speicher.entsperren(methode, geheimnis)

  const krypto = await e2ee()
  const geraete = await api.get<Array<{ id: string; isCurrent: boolean }>>('/devices')
  const eigenes = geraete.find((d) => d.isCurrent) ?? geraete[0]
  if (eigenes) await krypto.starten(personId, eigenes.id)

  await recoverySchluesselSicherstellen(speicher)
}

/**
 * Legt einmalig das Recovery-Schlüsselpaar dieses Geräts an.
 *
 * Ohne den öffentlichen Teil kann niemand einen Anker-Anteil FÜR uns
 * verschlüsseln — die Familie sähe uns in der Auswahl gar nicht erst. Der
 * private Teil bleibt im versiegelten Speicher; der Server bekommt ihn nie.
 *
 * Scheitert der Netzaufruf, ist das kein Grund, die Anmeldung abzubrechen:
 * beim nächsten Entsperren versucht es die App erneut.
 */
async function recoverySchluesselSicherstellen(
  speicher: typeof Keystore,
): Promise<void> {
  try {
    if (await speicher.holen('recovery-keypair')) return
    const { verwahrerSchluesselpaar } = await import('@fh/crypto')
    const paar = await verwahrerSchluesselpaar()
    const privat = new Uint8Array(await crypto.subtle.exportKey('pkcs8', paar.privateKey))
    await api.put('/recovery/public-key', { publicKey: paar.publicKey })
    await speicher.ablegen('recovery-keypair', { publicKey: paar.publicKey, privateKey: privat })
  } catch {
    // Nächster Versuch beim nächsten Entsperren.
  }
}

/**
 * Der Sicherheitsbereich lädt Anker und Sicherheitsnummer nach — beide
 * brauchen die Krypto-Bibliothek und haben im Startbundle nichts verloren.
 */
function SicherheitsBereich({ me }: { me: Me }) {
  const [Inhalt, setInhalt] = useState<preact.ComponentType<{ me: Me }> | null>(null)

  useEffect(() => {
    void import('./crypto/SicherheitsBereich.js').then((m) => setInhalt(() => m.SicherheitsBereich))
  }, [])

  if (!Inhalt) return <p class="fh-muted">Wird geladen…</p>
  return <Inhalt me={me} />
}

/** Kein leerer Bildschirm, während `/me` läuft (Anf. 71). */
function Startbildschirm() {
  return (
    <div class="fh-anmelden">
      <p class="fh-muted">Wird geladen…</p>
    </div>
  )
}

function rollenName(role: string): string {
  const namen: Record<string, string> = {
    admin: 'Administrator', parent: 'Elternteil', adult: 'Erwachsener',
    child: 'Kind', guest: 'Gast',
  }
  return namen[role] ?? role
}

function currentHash(): string {
  return location.hash.replace(/^#/, '')
}
